// @flow
import * as React from 'react';
import { Box } from 'grommet';
import { FormattedMessage, IntlShape, injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { withLeaflet } from 'react-leaflet';
import type { LeafletContext } from 'react-leaflet/lib/types';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import cableAndFiberMessages from 'esm-web-pro/messages/cableAndFiberMessages';
import MeatballsIcon from 'esm-web-pro/components/layout/sidePanel/icons/MeatballsIcon';
import RowActions from 'esm-web-pro/components/layout/sidePanel/RowActions';
import useHighlightFiber from 'esm-web-pro/containers/pages/network/objects/fiber/useHighlightFiber';
import { highlightFiberCircuit } from 'esm-web-pro/containers/pages/network/highlight';
import LabelText from 'esm-web-pro/components/layout/sidePanel/LabelText';
import { CableFiberColor, FiberTextContainer } from 'esm-web-pro/containers/pages/network/objects/cable/CableFiber';

type Props = {
    fibers: Array<Object>,
    spliceContainer: Object,
    intl: IntlShape,
    history: Object,
    match: Object,
    onView: Function,
    circuitPath: {
        [number]: Array<[number, number]>
    },
    clicked: string,
    leaflet: LeafletContext,
}

const SpliceContent = (props: Props) => {
    const {
        fibers, match, history, intl: { formatMessage }, onView, circuitPath: fibersPaths,
        clicked, spliceContainer, leaflet: { map },
    } = props;

    const [highlightFunction, highlightCleanUp] = useHighlightFiber();
    React.useEffect(() => () => highlightCleanUp(), []);

    const visualiseFiber = (e, fiber) => {
        const [fibersCallbacks, circuitPath] = highlightFiberCircuit(fibersPaths, fiber, map, highlightFunction);
        onView(`fiber-${fiber.id}`, fibersCallbacks, circuitPath);
    };

    return (
        <Box pad={{ vertical: '20px', horizontal: 'small' }} background="white" height={{ min: '100%' }}>
            <Box pad={{ horizontal: 'small', bottom: 'xsmall' }}>
                <LabelText>
                    <FormattedMessage id="splice-content-title" defaultMessage="Fibers" />
                </LabelText>
            </Box>
            <Box>
                <LabelText>
                    {fibers && (
                        fibers.map((fiber) => {
                            const cableSequence = (fiber.container.start_termination_point
                            && fiber.container.start_termination_point.id === spliceContainer.id)
                                ? fiber.container.start_sequence : fiber.container.end_sequence;

                            return (
                                <Box
                                    key={fiber.uuid}
                                    align="center"
                                    direction="row"
                                    hoverIndicator="light-2"
                                    onClick={(e) => visualiseFiber(e, fiber)}
                                    style={{ boxShadow: 'none' }}
                                    background={(`fiber-${fiber.id}` === clicked) ? 'light-2' : ''}
                                >
                                    <Box
                                        pad={{ horizontal: 'small', bottom: 'xsmall' }}
                                        align="center"
                                        direction="row"
                                        width="100%"
                                    >
                                        <FiberTextContainer>
                                            {`${formatMessage(fiber.container.patch
                                                ? cableAndFiberMessages.patchCableLong
                                                : cableAndFiberMessages.cableLong)} ${cableSequence} `}

                                            {(fiber.attributes?.color) && (
                                                <CableFiberColor
                                                    color={fiber.attributes.color}
                                                    stripeCount={fiber.attributes.stripe_count}
                                                    tubeNumber={fiber.attributes.tube_number}
                                                />
                                            )}

                                            {`${formatMessage(cableAndFiberMessages.fiberLong)} 
                                                ${fiber.sequence_number}`}
                                        </FiberTextContainer>
                                    </Box>
                                    <Box
                                        width="100%"
                                        align="center"
                                        direction="row"
                                        justify="end"
                                    >
                                        <RowActions
                                            actions={[{
                                                key: `${fiber.id}-more`,
                                                icon: <MeatballsIcon />,
                                                size: '20px',
                                                width: '3px',
                                                callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => {
                                                    e.stopPropagation();
                                                    const path = match.url.split('/layer');
                                                    history.push(`${path[0]}/layer/fiber/${fiber.identity}`);
                                                },
                                                tooltip: formatMessage(actionMessages.more),
                                            }]}
                                        />
                                    </Box>
                                </Box>
                            );
                        })
                    )}
                </LabelText>
            </Box>
        </Box>
    );
};

export default withLeaflet(injectIntl(withRouter(SpliceContent)));
